import { useEffect, useState, useRef } from 'react';
import { Link } from 'react-router-dom';
import Header from '@/pages/home/components/Header';
import Footer from '@/pages/home/components/Footer';

const SITE_URL = 'https://hoosierlifeinsurance.com';

// ─── Glossary Data ────────────────────────────────────────────────────────────
interface GlossaryTerm {
  term: string;
  letter: string;
  definition: string;
  example?: string;
  related?: string[];
  tag?: string;
}

const TERMS: GlossaryTerm[] = [
  // A
  {
    term: 'Accelerated Death Benefit (ADB)',
    letter: 'A',
    definition: 'A rider that lets you access a portion of your death benefit while still alive if you\'re diagnosed with a terminal illness (typically given 12–24 months to live). Most Indiana carriers include this at no extra cost.',
    example: 'An Indianapolis teacher diagnosed with stage 4 cancer accesses $150,000 of her $500,000 policy to pay for treatment and home care.',
    related: ['Rider', 'Terminal Illness Benefit'],
    tag: 'Rider',
  },
  {
    term: 'Accidental Death Benefit',
    letter: 'A',
    definition: 'A rider that pays an additional death benefit (often double or triple the face amount) if the insured dies as a result of an accident. Most financial advisors recommend buying more base coverage instead.',
    related: ['Rider', 'Face Amount'],
    tag: 'Rider',
  },
  {
    term: 'Actuarial Table',
    letter: 'A',
    definition: 'A statistical table used by insurance companies to calculate life expectancy and set premium rates based on age, gender, health, and other risk factors. Indiana carriers use these tables to price every policy.',
    related: ['Underwriting', 'Mortality Rate'],
    tag: 'Underwriting',
  },
  {
    term: 'Agent (Life Insurance)',
    letter: 'A',
    definition: 'A licensed professional who sells life insurance on behalf of one or more insurance companies. An independent agent (like Hoosier Life Insurance) represents multiple carriers and can shop the market for the best rate. A captive agent works for only one company.',
    example: 'An independent agent in Indianapolis can compare rates from Banner Life, Protective, and Mutual of Omaha simultaneously.',
    related: ['Independent Agent', 'Captive Agent', 'Broker'],
    tag: 'People',
  },
  {
    term: 'AM Best Rating',
    letter: 'A',
    definition: 'A financial strength rating assigned to insurance companies by AM Best, a credit rating agency. Ratings range from A++ (Superior) to D (Poor). Indiana residents should only buy from carriers rated A- or better.',
    example: 'Banner Life (A+), Protective Life (A+), Mutual of Omaha (A+) are all highly rated carriers available in Indiana.',
    related: ['Financial Strength Rating', 'Carrier'],
    tag: 'Carrier',
  },
  {
    term: 'Annuity',
    letter: 'A',
    definition: 'A financial product sold by insurance companies that provides a stream of income payments, either immediately or at a future date. Common in retirement planning for Indiana residents who want guaranteed income they can\'t outlive.',
    related: ['Fixed Annuity', 'Variable Annuity', 'Indexed Annuity'],
    tag: 'Product',
  },
  {
    term: 'Application',
    letter: 'A',
    definition: 'The formal document you complete when applying for life insurance. It includes personal information, health history, lifestyle questions, and beneficiary designations. Answers must be truthful — misrepresentation can void your policy.',
    related: ['Underwriting', 'Medical Exam'],
    tag: 'Process',
  },
  // B
  {
    term: 'Beneficiary',
    letter: 'B',
    definition: 'The person(s) or entity designated to receive the death benefit when the insured dies. You can name primary beneficiaries (first in line) and contingent beneficiaries (backup if primary predeceases you). Indiana law allows you to name anyone.',
    example: 'A Carmel homeowner names his wife as primary beneficiary (100%) and his two children as contingent beneficiaries (50% each).',
    related: ['Primary Beneficiary', 'Contingent Beneficiary', 'Death Benefit'],
    tag: 'Policy',
  },
  {
    term: 'Binding Receipt',
    letter: 'B',
    definition: 'A document that provides temporary life insurance coverage from the date of application while your full application is being underwritten. If you die during this period and are approved, your beneficiaries receive the full death benefit.',
    related: ['Conditional Receipt', 'Underwriting'],
    tag: 'Process',
  },
  {
    term: 'Buy-Sell Agreement',
    letter: 'B',
    definition: 'A legally binding agreement between business partners that outlines what happens to a partner\'s ownership share if they die, become disabled, or leave the business. Life insurance is commonly used to fund buy-sell agreements for Indiana businesses.',
    example: 'Two Indianapolis business partners each own $500K policies on each other. If one dies, the survivor uses the death benefit to buy out the deceased partner\'s share from their estate.',
    related: ['Key Person Insurance', 'Business Life Insurance'],
    tag: 'Business',
  },
  // C
  {
    term: 'Cash Value',
    letter: 'C',
    definition: 'The savings component of permanent life insurance policies (whole life, universal life, IUL). A portion of each premium builds tax-deferred cash value that you can borrow against or withdraw. Term life insurance has no cash value.',
    related: ['Whole Life Insurance', 'Universal Life Insurance', 'Policy Loan'],
    tag: 'Policy',
  },
  {
    term: 'Carrier',
    letter: 'C',
    definition: 'The insurance company that issues and backs your life insurance policy. Top carriers available to Indiana residents include Banner Life, Protective Life, Mutual of Omaha, Pacific Life, and Transamerica.',
    related: ['AM Best Rating', 'Financial Strength Rating'],
    tag: 'Carrier',
  },
  {
    term: 'Chronic Illness Rider',
    letter: 'C',
    definition: 'A rider that allows you to access your death benefit if you\'re diagnosed with a chronic illness and can\'t perform 2 of 6 Activities of Daily Living (ADLs). Similar to a long-term care rider but typically less expensive.',
    related: ['Long-Term Care Rider', 'Accelerated Death Benefit', 'Rider'],
    tag: 'Rider',
  },
  {
    term: 'Contestability Period',
    letter: 'C',
    definition: 'The first two years of a life insurance policy during which the insurance company can investigate and potentially deny a claim if material misrepresentation is found on the application. After two years, the policy is generally incontestable.',
    related: ['Incontestability Clause', 'Misrepresentation'],
    tag: 'Policy',
  },
  {
    term: 'Contingent Beneficiary',
    letter: 'C',
    definition: 'The backup beneficiary who receives the death benefit if the primary beneficiary predeceases the insured or cannot be located. Always name a contingent beneficiary to avoid your death benefit going through probate.',
    related: ['Primary Beneficiary', 'Beneficiary'],
    tag: 'Policy',
  },
  {
    term: 'Conversion Privilege',
    letter: 'C',
    definition: 'The right to convert a term life insurance policy to a permanent policy (whole life or universal life) without a new medical exam, regardless of your current health. Most Indiana term policies include this feature — check the conversion deadline.',
    example: 'A Fishers resident diagnosed with diabetes at age 42 converts his 20-year term policy to whole life before the conversion deadline, locking in permanent coverage despite his new health condition.',
    related: ['Term Life Insurance', 'Whole Life Insurance'],
    tag: 'Policy',
  },
  // D
  {
    term: 'Death Benefit',
    letter: 'D',
    definition: 'The amount of money paid to your beneficiaries when you die. Also called the face amount or face value. Life insurance death benefits are generally income-tax-free to beneficiaries under federal and Indiana state law.',
    related: ['Face Amount', 'Beneficiary'],
    tag: 'Policy',
  },
  {
    term: 'DIME Method',
    letter: 'D',
    definition: 'A formula for calculating how much life insurance you need: Debt + Income replacement + Mortgage + Education. Add up all four categories to find your total coverage need, then subtract existing assets and coverage.',
    example: 'An Indianapolis family with $225K in debts, $900K income need, $0 additional mortgage, and $200K education fund needs $1.325M in coverage.',
    related: ['Needs Analysis', 'Face Amount'],
    tag: 'Planning',
  },
  {
    term: 'Dividend',
    letter: 'D',
    definition: 'A portion of the insurance company\'s profits returned to policyholders of participating whole life policies. Dividends are not guaranteed but have been paid consistently by mutual companies like Northwestern Mutual and MassMutual for over 100 years.',
    related: ['Participating Policy', 'Whole Life Insurance', 'Mutual Company'],
    tag: 'Policy',
  },
  // E
  {
    term: 'Elimination Period',
    letter: 'E',
    definition: 'The waiting period before benefits begin on a disability or long-term care policy. Common elimination periods are 30, 60, 90, or 180 days. A longer elimination period lowers your premium.',
    related: ['Long-Term Care Rider', 'Disability Insurance'],
    tag: 'Policy',
  },
  {
    term: 'Endowment',
    letter: 'E',
    definition: 'A life insurance policy that pays the face amount either upon the insured\'s death or when the policy matures (reaches a specific age, typically 100). Rarely sold today but still found in older policies.',
    related: ['Whole Life Insurance', 'Face Amount'],
    tag: 'Product',
  },
  {
    term: 'Evidence of Insurability',
    letter: 'E',
    definition: 'Proof that you are insurable, typically through a medical exam, health questionnaire, or medical records review. Required when applying for most life insurance policies or increasing coverage amounts.',
    related: ['Medical Exam', 'Underwriting', 'Guaranteed Insurability Rider'],
    tag: 'Underwriting',
  },
  {
    term: 'Exclusion',
    letter: 'E',
    definition: 'A specific condition, circumstance, or cause of death that is not covered by a life insurance policy. Common exclusions include suicide within the first two years and death resulting from illegal activities.',
    related: ['Contestability Period', 'Policy'],
    tag: 'Policy',
  },
  // F
  {
    term: 'Face Amount',
    letter: 'F',
    definition: 'The stated death benefit of a life insurance policy — the amount your beneficiaries receive when you die. Also called the face value or coverage amount. For a $500,000 policy, the face amount is $500,000.',
    related: ['Death Benefit', 'Coverage Amount'],
    tag: 'Policy',
  },
  {
    term: 'Final Expense Insurance',
    letter: 'F',
    definition: 'A small whole life insurance policy (typically $5,000–$50,000) designed to cover funeral costs, burial expenses, and end-of-life medical bills. Popular among Indiana seniors aged 50–85. Premiums are fixed and coverage is permanent.',
    example: 'An 68-year-old Indianapolis retiree buys a $15,000 final expense policy for $62/month to cover her funeral costs and spare her children the financial burden.',
    related: ['Whole Life Insurance', 'Guaranteed Issue Life Insurance', 'Burial Insurance'],
    tag: 'Product',
  },
  {
    term: 'Free Look Period',
    letter: 'F',
    definition: 'A period (typically 10–30 days in Indiana) after receiving your policy during which you can cancel for any reason and receive a full premium refund. Indiana law requires a minimum 10-day free look period.',
    related: ['Policy', 'Premium'],
    tag: 'Policy',
  },
  // G
  {
    term: 'Grace Period',
    letter: 'G',
    definition: 'The period after a missed premium payment during which your policy remains in force. Indiana law requires a minimum 30-day grace period for life insurance policies. If you die during the grace period, the overdue premium is deducted from the death benefit.',
    related: ['Premium', 'Lapse'],
    tag: 'Policy',
  },
  {
    term: 'Group Life Insurance',
    letter: 'G',
    definition: 'Life insurance provided through an employer or organization that covers a group of people under a single master policy. Typically 1–2x salary. Coverage ends when you leave the group. Should be supplemented with individual coverage.',
    related: ['Term Life Insurance', 'Portability'],
    tag: 'Product',
  },
  {
    term: 'Guaranteed Issue Life Insurance',
    letter: 'G',
    definition: 'A type of whole life insurance that requires no medical exam and no health questions — acceptance is guaranteed regardless of health. Available to Indiana residents aged 50–85, typically in amounts up to $25,000. Premiums are higher and a graded death benefit applies in the first 2 years.',
    example: 'A 72-year-old Indianapolis man with COPD who was declined for traditional coverage qualifies for a $10,000 guaranteed issue policy at $87/month.',
    related: ['Final Expense Insurance', 'Graded Death Benefit', 'Simplified Issue'],
    tag: 'Product',
  },
  {
    term: 'Guaranteed Insurability Rider',
    letter: 'G',
    definition: 'A rider that allows you to purchase additional life insurance coverage at specific future dates (marriage, birth of child, policy anniversary) without a new medical exam, regardless of your health at that time.',
    related: ['Rider', 'Evidence of Insurability'],
    tag: 'Rider',
  },
  // H
  {
    term: 'Health Classification',
    letter: 'H',
    definition: 'The risk category assigned to you by the underwriter based on your health, lifestyle, and medical history. Common classes from best to worst: Preferred Plus, Preferred, Standard Plus, Standard, Substandard (Table Rated). Your class determines your premium.',
    related: ['Underwriting', 'Table Rating', 'Premium'],
    tag: 'Underwriting',
  },
  // I
  {
    term: 'Incontestability Clause',
    letter: 'I',
    definition: 'A provision in life insurance policies that prevents the insurer from voiding the policy or denying a claim based on misrepresentation after the policy has been in force for two years (one year in some states). Indiana follows the standard two-year rule.',
    related: ['Contestability Period', 'Misrepresentation'],
    tag: 'Policy',
  },
  {
    term: 'Indexed Universal Life (IUL)',
    letter: 'I',
    definition: 'A type of permanent life insurance where the cash value growth is linked to a stock market index (like the S&P 500), with a floor (typically 0%) protecting against losses and a cap limiting gains. Offers more growth potential than whole life with downside protection.',
    related: ['Universal Life Insurance', 'Cash Value', 'Whole Life Insurance'],
    tag: 'Product',
  },
  {
    term: 'Insurable Interest',
    letter: 'I',
    definition: 'A legal requirement that the policy owner must have a financial or emotional stake in the continued life of the insured. You automatically have insurable interest in yourself, your spouse, and your children. Business partners have insurable interest in each other.',
    related: ['Policy Owner', 'Insured'],
    tag: 'Policy',
  },
  {
    term: 'Insured',
    letter: 'I',
    definition: 'The person whose life is covered by the life insurance policy. When the insured dies, the death benefit is paid to the beneficiary. The insured and the policy owner can be different people.',
    related: ['Policy Owner', 'Beneficiary'],
    tag: 'People',
  },
  {
    term: 'Irrevocable Beneficiary',
    letter: 'I',
    definition: 'A beneficiary designation that cannot be changed without the beneficiary\'s written consent. Rarely used but sometimes required in divorce settlements or business agreements in Indiana.',
    related: ['Beneficiary', 'Revocable Beneficiary'],
    tag: 'Policy',
  },
  // K
  {
    term: 'Key Person Insurance',
    letter: 'K',
    definition: 'Life insurance purchased by a business on the life of a key employee or owner whose death would significantly impact the company\'s finances. The business pays the premiums and is the beneficiary. Common for Indiana small businesses and partnerships.',
    example: 'An Indianapolis tech startup buys a $1M policy on its CTO. If the CTO dies, the company uses the death benefit to recruit a replacement and cover lost revenue during the transition.',
    related: ['Buy-Sell Agreement', 'Business Life Insurance'],
    tag: 'Business',
  },
  // L
  {
    term: 'Lapse',
    letter: 'L',
    definition: 'The termination of a life insurance policy due to non-payment of premiums after the grace period expires. A lapsed policy provides no coverage. Some policies have non-forfeiture options that prevent a complete lapse.',
    related: ['Grace Period', 'Non-Forfeiture Options', 'Premium'],
    tag: 'Policy',
  },
  {
    term: 'Level Premium',
    letter: 'L',
    definition: 'A premium that remains the same throughout the life of the policy. Most term life and whole life policies in Indiana have level premiums, making budgeting predictable.',
    related: ['Premium', 'Term Life Insurance', 'Whole Life Insurance'],
    tag: 'Policy',
  },
  {
    term: 'Life Settlement',
    letter: 'L',
    definition: 'The sale of an existing life insurance policy to a third party for more than the cash surrender value but less than the death benefit. The buyer pays future premiums and collects the death benefit. Available to Indiana policyholders who no longer need or can afford their coverage.',
    related: ['Cash Value', 'Viatical Settlement'],
    tag: 'Policy',
  },
  {
    term: 'Long-Term Care Rider',
    letter: 'L',
    definition: 'A rider that allows you to access your death benefit to pay for long-term care expenses (nursing home, assisted living, home health care) if you can\'t perform 2 of 6 Activities of Daily Living. A cost-effective alternative to standalone LTC insurance.',
    related: ['Rider', 'Chronic Illness Rider', 'Accelerated Death Benefit'],
    tag: 'Rider',
  },
  // M
  {
    term: 'Medical Exam (Paramedical Exam)',
    letter: 'M',
    definition: 'A health examination required by most traditional life insurance applications. Typically includes blood draw, urine sample, blood pressure, height/weight, and health history questions. Usually conducted by a nurse at your home or office at no cost to you.',
    related: ['Underwriting', 'No-Exam Life Insurance', 'Evidence of Insurability'],
    tag: 'Underwriting',
  },
  {
    term: 'Misrepresentation',
    letter: 'M',
    definition: 'Providing false or incomplete information on a life insurance application. Material misrepresentation (information that would have changed the underwriting decision) can result in policy cancellation or claim denial during the contestability period.',
    related: ['Contestability Period', 'Application', 'Incontestability Clause'],
    tag: 'Policy',
  },
  {
    term: 'Mortality Rate',
    letter: 'M',
    definition: 'The statistical probability of death within a given population over a specific time period. Insurance companies use mortality rates to calculate premiums. Indiana\'s mortality rates are slightly above the national average due to higher rates of obesity and smoking.',
    related: ['Actuarial Table', 'Underwriting'],
    tag: 'Underwriting',
  },
  {
    term: 'Mortgage Protection Insurance',
    letter: 'M',
    definition: 'A type of life insurance specifically designed to pay off your mortgage if you die. Can be a decreasing term policy (coverage decreases as your mortgage balance decreases) or a level term policy. Indiana homeowners often use standard term life instead, which offers more flexibility.',
    related: ['Term Life Insurance', 'Decreasing Term'],
    tag: 'Product',
  },
  {
    term: 'Mutual Company',
    letter: 'M',
    definition: 'An insurance company owned by its policyholders rather than shareholders. Mutual companies can pay dividends to policyholders. Examples include Northwestern Mutual, MassMutual, and New York Life — all available in Indiana.',
    related: ['Dividend', 'Stock Company', 'Participating Policy'],
    tag: 'Carrier',
  },
  // N
  {
    term: 'Named Insured',
    letter: 'N',
    definition: 'The person specifically identified in the policy as the insured. The named insured\'s death triggers the death benefit payment to the beneficiary.',
    related: ['Insured', 'Policy Owner'],
    tag: 'People',
  },
  {
    term: 'Needs Analysis',
    letter: 'N',
    definition: 'A comprehensive assessment of how much life insurance coverage a person or family needs, taking into account income, debts, dependents, assets, and financial goals. The DIME method is a common needs analysis framework used by Indiana agents.',
    related: ['DIME Method', 'Face Amount'],
    tag: 'Planning',
  },
  {
    term: 'No-Exam Life Insurance',
    letter: 'N',
    definition: 'Life insurance that doesn\'t require a traditional medical exam. Includes accelerated underwriting (uses data algorithms), simplified issue (health questionnaire only), and guaranteed issue (no questions asked). Available from many Indiana carriers up to $1M+ for healthy applicants.',
    related: ['Accelerated Underwriting', 'Simplified Issue', 'Guaranteed Issue Life Insurance'],
    tag: 'Product',
  },
  {
    term: 'Non-Forfeiture Options',
    letter: 'N',
    definition: 'Options available to policyholders who stop paying premiums on a permanent life insurance policy. Common options include: cash surrender value (take the cash), reduced paid-up insurance (smaller permanent policy, no more premiums), or extended term insurance (same death benefit for a shorter term).',
    related: ['Cash Value', 'Lapse', 'Whole Life Insurance'],
    tag: 'Policy',
  },
  // P
  {
    term: 'Paid-Up Additions (PUA)',
    letter: 'P',
    definition: 'Additional whole life insurance purchased with dividends or extra premium payments. PUAs increase both the death benefit and cash value of a whole life policy and are themselves paid-up (require no future premiums). A key tool in infinite banking strategies.',
    related: ['Dividend', 'Whole Life Insurance', 'Cash Value'],
    tag: 'Policy',
  },
  {
    term: 'Permanent Life Insurance',
    letter: 'P',
    definition: 'Life insurance that provides coverage for your entire life (as long as premiums are paid) and builds cash value. Includes whole life, universal life, and indexed universal life. More expensive than term but provides lifelong protection and a savings component.',
    related: ['Whole Life Insurance', 'Universal Life Insurance', 'Term Life Insurance'],
    tag: 'Product',
  },
  {
    term: 'Policy Loan',
    letter: 'P',
    definition: 'A loan taken against the cash value of a permanent life insurance policy. Policy loans are not taxable, require no credit check, and have no repayment schedule — but unpaid loans plus interest reduce the death benefit. Available on whole life and universal life policies.',
    related: ['Cash Value', 'Whole Life Insurance'],
    tag: 'Policy',
  },
  {
    term: 'Policy Owner',
    letter: 'P',
    definition: 'The person or entity that owns the life insurance policy, pays the premiums, and has the right to make changes (change beneficiaries, take loans, surrender the policy). The policy owner and the insured can be different people.',
    related: ['Insured', 'Beneficiary', 'Insurable Interest'],
    tag: 'People',
  },
  {
    term: 'Premium',
    letter: 'P',
    definition: 'The amount you pay for life insurance coverage, typically monthly, quarterly, or annually. Premiums are determined by your age, health, coverage amount, policy type, and term length. Paying annually instead of monthly saves 5–8% with most Indiana carriers.',
    related: ['Level Premium', 'Health Classification'],
    tag: 'Policy',
  },
  {
    term: 'Primary Beneficiary',
    letter: 'P',
    definition: 'The first person or entity in line to receive the death benefit when the insured dies. You can name multiple primary beneficiaries and specify the percentage each receives (e.g., spouse 60%, child 40%).',
    related: ['Contingent Beneficiary', 'Beneficiary', 'Death Benefit'],
    tag: 'Policy',
  },
  // R
  {
    term: 'Rate Class',
    letter: 'R',
    definition: 'See Health Classification. The underwriting category that determines your premium. Common classes: Preferred Plus (best rates), Preferred, Standard Plus, Standard, Substandard. Smokers are placed in a separate Tobacco class.',
    related: ['Health Classification', 'Underwriting', 'Premium'],
    tag: 'Underwriting',
  },
  {
    term: 'Renewable Term',
    letter: 'R',
    definition: 'A term life insurance policy that can be renewed at the end of the term without a new medical exam, though at a higher premium based on your age at renewal. Most Indiana term policies are renewable but it\'s usually better to buy a new policy if you\'re still healthy.',
    related: ['Term Life Insurance', 'Conversion Privilege'],
    tag: 'Product',
  },
  {
    term: 'Return of Premium (ROP)',
    letter: 'R',
    definition: 'A term life insurance rider that refunds all premiums paid if you outlive the policy term. Sounds appealing but costs 30–50% more than standard term. The math rarely favors ROP over investing the premium difference.',
    related: ['Rider', 'Term Life Insurance', 'Premium'],
    tag: 'Rider',
  },
  {
    term: 'Rider',
    letter: 'R',
    definition: 'An optional add-on to a life insurance policy that modifies or enhances coverage. Common riders include accelerated death benefit, waiver of premium, child term, and guaranteed insurability. Some riders are free; others add to your premium.',
    related: ['Accelerated Death Benefit', 'Waiver of Premium', 'Child Term Rider'],
    tag: 'Policy',
  },
  // S
  {
    term: 'Simplified Issue Life Insurance',
    letter: 'S',
    definition: 'Life insurance that requires a health questionnaire but no medical exam. Faster approval (often same-day) but slightly higher premiums than fully underwritten policies. Available from many Indiana carriers up to $500,000.',
    related: ['No-Exam Life Insurance', 'Guaranteed Issue Life Insurance', 'Underwriting'],
    tag: 'Product',
  },
  {
    term: 'Substandard Risk',
    letter: 'S',
    definition: 'An applicant who presents a higher-than-average risk due to health conditions, dangerous occupation, or lifestyle factors. Substandard applicants may be approved with a table rating (higher premium) or declined. Indiana carriers vary significantly in how they handle substandard risks.',
    related: ['Table Rating', 'Health Classification', 'Underwriting'],
    tag: 'Underwriting',
  },
  {
    term: 'Suicide Clause',
    letter: 'S',
    definition: 'A standard policy provision that excludes death by suicide during the first two years of the policy. After two years, suicide is covered like any other cause of death. Indiana follows the standard two-year suicide exclusion.',
    related: ['Exclusion', 'Contestability Period'],
    tag: 'Policy',
  },
  {
    term: 'Surrender Value',
    letter: 'S',
    definition: 'The amount you receive if you cancel (surrender) a permanent life insurance policy. Equal to the cash value minus any surrender charges. Surrender charges typically apply during the first 7–15 years of a policy.',
    related: ['Cash Value', 'Non-Forfeiture Options'],
    tag: 'Policy',
  },
  // T
  {
    term: 'Table Rating',
    letter: 'T',
    definition: 'A premium surcharge applied to applicants with health conditions that make them a higher risk. Table ratings are expressed as numbers (Table 2, Table 4) or letters (Table B, Table D). Each table typically adds 25% to the standard premium.',
    example: 'A Noblesville applicant with well-controlled Type 2 diabetes is approved at Table 2, meaning his premium is 50% higher than the standard rate.',
    related: ['Substandard Risk', 'Health Classification', 'Underwriting'],
    tag: 'Underwriting',
  },
  {
    term: 'Term Life Insurance',
    letter: 'T',
    definition: 'Life insurance that provides coverage for a specific period (term) — typically 10, 15, 20, or 30 years. If you die during the term, your beneficiaries receive the death benefit. If you outlive the term, coverage ends with no payout. The most affordable type of life insurance.',
    example: 'A 35-year-old Indianapolis father buys a $1M 20-year term policy for $52/month. His family is protected until his youngest child finishes college.',
    related: ['Permanent Life Insurance', 'Whole Life Insurance', 'Conversion Privilege'],
    tag: 'Product',
  },
  {
    term: 'Terminal Illness Benefit',
    letter: 'T',
    definition: 'See Accelerated Death Benefit. A benefit that allows terminally ill policyholders to access a portion of their death benefit while still alive.',
    related: ['Accelerated Death Benefit', 'Rider'],
    tag: 'Rider',
  },
  {
    term: 'Trust (Life Insurance Trust)',
    letter: 'T',
    definition: 'A legal arrangement where a trust owns a life insurance policy. An Irrevocable Life Insurance Trust (ILIT) keeps the death benefit out of your taxable estate. Relevant for Indiana high-net-worth individuals with estates over the federal exemption ($13.6M in 2024).',
    related: ['Beneficiary', 'Estate Planning'],
    tag: 'Planning',
  },
  // U
  {
    term: 'Underwriter',
    letter: 'U',
    definition: 'The insurance company professional who evaluates your application, reviews your health and lifestyle information, and determines your risk classification and premium. Underwriters decide whether to approve, rate, or decline your application.',
    related: ['Underwriting', 'Health Classification', 'Table Rating'],
    tag: 'People',
  },
  {
    term: 'Underwriting',
    letter: 'U',
    definition: 'The process by which an insurance company evaluates the risk of insuring an applicant and determines the appropriate premium. Involves reviewing medical records, exam results, prescription history, driving record, and financial information.',
    related: ['Underwriter', 'Medical Exam', 'Health Classification'],
    tag: 'Underwriting',
  },
  {
    term: 'Universal Life Insurance (UL)',
    letter: 'U',
    definition: 'A flexible permanent life insurance policy that allows you to adjust your premium payments and death benefit within certain limits. Builds cash value that earns interest based on current market rates. More flexible than whole life but requires careful management to avoid lapse.',
    related: ['Whole Life Insurance', 'Indexed Universal Life', 'Cash Value'],
    tag: 'Product',
  },
  // V
  {
    term: 'Variable Life Insurance',
    letter: 'V',
    definition: 'A permanent life insurance policy where the cash value is invested in sub-accounts (similar to mutual funds). The death benefit and cash value fluctuate based on investment performance. Higher risk and reward than whole life or universal life.',
    related: ['Universal Life Insurance', 'Cash Value', 'Permanent Life Insurance'],
    tag: 'Product',
  },
  {
    term: 'Viatical Settlement',
    letter: 'V',
    definition: 'The sale of a life insurance policy by a terminally ill person to a third party for a lump sum less than the death benefit. The buyer pays future premiums and collects the death benefit when the insured dies. Different from a life settlement in that the insured is terminally ill.',
    related: ['Life Settlement', 'Accelerated Death Benefit'],
    tag: 'Policy',
  },
  // W
  {
    term: 'Waiver of Premium Rider',
    letter: 'W',
    definition: 'A rider that waives your life insurance premiums if you become totally disabled and unable to work. Your policy stays in force at no cost during the disability period. Highly recommended for Indiana working adults — disability is far more common than premature death.',
    related: ['Rider', 'Disability Insurance'],
    tag: 'Rider',
  },
  {
    term: 'Whole Life Insurance',
    letter: 'W',
    definition: 'A type of permanent life insurance that provides coverage for your entire life with fixed premiums, a guaranteed death benefit, and guaranteed cash value growth. More expensive than term but offers lifelong protection and a conservative savings component.',
    example: 'A 40-year-old Carmel professional buys a $500K whole life policy for $450/month. Her premiums never increase, her cash value grows tax-deferred, and her family is protected for life.',
    related: ['Term Life Insurance', 'Cash Value', 'Dividend', 'Universal Life Insurance'],
    tag: 'Product',
  },
];

const LETTERS = 'ABCDEFGHIKLMNPRSTUVW'.split('');
const TAG_COLORS: Record<string, string> = {
  'Product': 'bg-[#1A3A52] text-white',
  'Rider': 'bg-[#D97642] text-white',
  'Policy': 'bg-teal-600 text-white',
  'Underwriting': 'bg-amber-600 text-white',
  'Planning': 'bg-emerald-600 text-white',
  'Business': 'bg-indigo-600 text-white',
  'Carrier': 'bg-rose-600 text-white',
  'People': 'bg-gray-600 text-white',
  'Process': 'bg-cyan-600 text-white',
};

export default function LifeInsuranceGlossaryIndiana() {
  const [search, setSearch] = useState('');
  const [activeTag, setActiveTag] = useState('All');
  const [activeLetter, setActiveLetter] = useState('');
  const letterRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const allTags = ['All', ...Array.from(new Set(TERMS.map(t => t.tag || 'Other'))).sort()];

  const filtered = TERMS.filter(t => {
    const matchSearch = search === '' ||
      t.term.toLowerCase().includes(search.toLowerCase()) ||
      t.definition.toLowerCase().includes(search.toLowerCase());
    const matchTag = activeTag === 'All' || t.tag === activeTag;
    const matchLetter = activeLetter === '' || t.letter === activeLetter;
    return matchSearch && matchTag && matchLetter;
  });

  const groupedByLetter = filtered.reduce<Record<string, GlossaryTerm[]>>((acc, term) => {
    if (!acc[term.letter]) acc[term.letter] = [];
    acc[term.letter].push(term);
    return acc;
  }, {});

  const visibleLetters = Object.keys(groupedByLetter).sort();

  const scrollToLetter = (letter: string) => {
    setActiveLetter(letter === activeLetter ? '' : letter);
    const el = letterRefs.current[letter];
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  useEffect(() => {
    document.title = 'Life Insurance Glossary Indiana — 55+ Terms Defined in Plain English | Hoosier Life Insurance';
    const metaDesc = document.querySelector('meta[name="description"]');
    if (metaDesc) metaDesc.setAttribute('content', 'Complete life insurance glossary for Indiana residents. 55+ terms defined in plain English — from accelerated death benefit to whole life insurance. No jargon, just clear explanations.');
    const metaKw = document.querySelector('meta[name="keywords"]');
    if (metaKw) metaKw.setAttribute('content', 'life insurance glossary Indiana, life insurance terms Indiana, life insurance definitions, insurance terminology Indianapolis');

    let canonical = document.querySelector('link[rel="canonical"]') as HTMLLinkElement | null;
    if (!canonical) { canonical = document.createElement('link'); canonical.rel = 'canonical'; document.head.appendChild(canonical); }
    canonical.href = `${SITE_URL}/resources/life-insurance-glossary-indiana`;

    const schemas = [
      {
        '@context': 'https://schema.org',
        '@type': 'DefinedTermSet',
        name: 'Indiana Life Insurance Glossary',
        description: '55+ life insurance terms defined in plain English for Indiana residents.',
        url: `${SITE_URL}/resources/life-insurance-glossary-indiana`,
        inLanguage: 'en-US',
        publisher: { '@type': 'Organization', name: 'Hoosier Life Insurance', url: SITE_URL },
        hasDefinedTerm: TERMS.map(t => ({
          '@type': 'DefinedTerm',
          name: t.term,
          description: t.definition,
          inDefinedTermSet: `${SITE_URL}/resources/life-insurance-glossary-indiana`,
        })),
      },
      {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: SITE_URL },
          { '@type': 'ListItem', position: 2, name: 'Resources', item: `${SITE_URL}/resources` },
          { '@type': 'ListItem', position: 3, name: 'Life Insurance Glossary Indiana', item: `${SITE_URL}/resources/life-insurance-glossary-indiana` },
        ],
      },
      {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: [
          {
            '@type': 'Question',
            name: 'What is the difference between term and whole life insurance in Indiana?',
            acceptedAnswer: { '@type': 'Answer', text: 'Term life insurance provides coverage for a specific period (10, 15, 20, or 30 years) and is the most affordable option. Whole life insurance provides permanent coverage for your entire life and builds cash value. Most Indiana families benefit most from term life insurance for income replacement and whole life for permanent needs like final expenses or estate planning.' },
          },
          {
            '@type': 'Question',
            name: 'What is a beneficiary in life insurance?',
            acceptedAnswer: { '@type': 'Answer', text: 'A beneficiary is the person or entity designated to receive the death benefit when the insured dies. You can name primary beneficiaries (first in line) and contingent beneficiaries (backup). Indiana law allows you to name anyone as a beneficiary, and death benefits are generally income-tax-free.' },
          },
          {
            '@type': 'Question',
            name: 'What does underwriting mean in life insurance?',
            acceptedAnswer: { '@type': 'Answer', text: 'Underwriting is the process by which an insurance company evaluates your risk and determines your premium. It involves reviewing your medical history, exam results, prescription records, driving record, and financial information. Your underwriting result determines your health classification (Preferred Plus, Preferred, Standard, etc.) which directly sets your premium.' },
          },
        ],
      },
    ];

    schemas.forEach((schema, i) => {
      let script = document.getElementById(`schema-glossary-${i}`) as HTMLScriptElement | null;
      if (!script) { script = document.createElement('script'); script.id = `schema-glossary-${i}`; script.type = 'application/ld+json'; document.head.appendChild(script); }
      script.textContent = JSON.stringify(schema);
    });

    return () => { schemas.forEach((_, i) => document.getElementById(`schema-glossary-${i}`)?.remove()); };
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />

      {/* Hero */}
      <section className="relative bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] pt-32 pb-16 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 right-20 w-72 h-72 rounded-full bg-[#D97642] blur-3xl" />
          <div className="absolute bottom-0 left-10 w-96 h-96 rounded-full bg-teal-400 blur-3xl" />
        </div>
        <div className="relative max-w-5xl mx-auto px-6">
          <div className="mb-4">
            <Link to="/resources" className="inline-flex items-center text-white/60 hover:text-white transition-colors text-sm">
              <i className="ri-arrow-left-line mr-2" />Back to Resources
            </Link>
          </div>
          <div className="inline-flex items-center gap-2 bg-[#D97642]/20 text-[#D97642] border border-[#D97642]/30 rounded-full px-4 py-1.5 text-sm font-semibold mb-6">
            <i className="ri-book-open-line" />Reference Guide
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-5 leading-tight">
            Life Insurance Glossary<br />for Indiana Residents
          </h1>
          <p className="text-xl text-white/75 mb-8 max-w-2xl leading-relaxed">
            {TERMS.length}+ life insurance terms defined in plain English — no jargon, no confusion. Indiana-specific context on every definition.
          </p>
          <div className="flex flex-wrap gap-6 text-sm text-white/60">
            <span className="flex items-center gap-2"><i className="ri-check-double-line" />{TERMS.length} terms defined</span>
            <span className="flex items-center gap-2"><i className="ri-map-pin-line" />Indiana-specific context</span>
            <span className="flex items-center gap-2"><i className="ri-search-line" />Searchable &amp; filterable</span>
          </div>
        </div>
      </section>

      {/* Search + Filter Bar */}
      <section className="sticky top-0 z-20 bg-white border-b border-gray-200 py-4 shadow-sm">
        <div className="max-w-5xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row gap-3">
            {/* Search */}
            <div className="relative flex-1">
              <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search terms (e.g. beneficiary, cash value, rider...)"
                value={search}
                onChange={e => { setSearch(e.target.value); setActiveLetter(''); }}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-[#D97642] transition-colors"
              />
              {search && (
                <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer">
                  <i className="ri-close-line" />
                </button>
              )}
            </div>
            {/* Tag Filter */}
            <div className="flex gap-2 overflow-x-auto pb-0.5">
              {allTags.map(tag => (
                <button
                  key={tag}
                  onClick={() => { setActiveTag(tag); setActiveLetter(''); }}
                  className={`whitespace-nowrap px-3 py-2 rounded-lg text-xs font-semibold transition-all cursor-pointer ${activeTag === tag ? 'bg-[#1A3A52] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </div>
      </section>

      <div className="max-w-5xl mx-auto px-6 py-12">
        <div className="flex gap-8">

          {/* A-Z Sidebar */}
          <aside className="hidden lg:block w-16 flex-shrink-0">
            <div className="sticky top-32">
              <div className="flex flex-col gap-1">
                {LETTERS.map(letter => {
                  const hasTerms = visibleLetters.includes(letter);
                  return (
                    <button
                      key={letter}
                      onClick={() => hasTerms && scrollToLetter(letter)}
                      className={`w-10 h-10 rounded-lg text-sm font-bold transition-all cursor-pointer ${
                        activeLetter === letter ? 'bg-[#D97642] text-white' :
                        hasTerms ? 'text-[#1A3A52] hover:bg-[#1A3A52]/10' :
                        'text-gray-300 cursor-default'
                      }`}
                    >
                      {letter}
                    </button>
                  );
                })}
              </div>
            </div>
          </aside>

          {/* Main Content */}
          <main className="flex-1 min-w-0">
            {/* Stats */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="bg-[#F9F7F4] rounded-xl px-5 py-3 text-center">
                <p className="text-xl font-bold text-[#D97642]">{filtered.length}</p>
                <p className="text-xs text-gray-500">Terms shown</p>
              </div>
              <div className="bg-[#F9F7F4] rounded-xl px-5 py-3 text-center">
                <p className="text-xl font-bold text-[#1A3A52]">{visibleLetters.length}</p>
                <p className="text-xs text-gray-500">Letters</p>
              </div>
              {(search || activeTag !== 'All' || activeLetter) && (
                <button
                  onClick={() => { setSearch(''); setActiveTag('All'); setActiveLetter(''); }}
                  className="flex items-center gap-2 bg-gray-100 hover:bg-gray-200 text-gray-600 rounded-xl px-4 py-3 text-sm font-semibold transition-colors cursor-pointer"
                >
                  <i className="ri-refresh-line" />Clear filters
                </button>
              )}
            </div>

            {/* A-Z Mobile Nav */}
            <div className="lg:hidden flex gap-1 flex-wrap mb-6">
              {LETTERS.map(letter => {
                const hasTerms = visibleLetters.includes(letter);
                return (
                  <button
                    key={letter}
                    onClick={() => hasTerms && scrollToLetter(letter)}
                    className={`w-8 h-8 rounded-lg text-xs font-bold transition-all cursor-pointer ${
                      activeLetter === letter ? 'bg-[#D97642] text-white' :
                      hasTerms ? 'bg-[#1A3A52]/10 text-[#1A3A52]' :
                      'text-gray-300 cursor-default'
                    }`}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>

            {filtered.length === 0 ? (
              <div className="text-center py-20">
                <i className="ri-search-line text-5xl text-gray-200 block mb-4" />
                <p className="text-xl text-gray-500">No terms found for &ldquo;{search}&rdquo;</p>
                <button onClick={() => { setSearch(''); setActiveTag('All'); setActiveLetter(''); }} className="mt-4 text-[#D97642] font-semibold hover:underline cursor-pointer text-sm">Clear all filters</button>
              </div>
            ) : (
              <div className="space-y-12">
                {visibleLetters.map(letter => (
                  <div
                    key={letter}
                    ref={el => { letterRefs.current[letter] = el; }}
                    className="scroll-mt-32"
                  >
                    {/* Letter Header */}
                    <div className="flex items-center gap-4 mb-6">
                      <div className="w-12 h-12 bg-[#1A3A52] text-white rounded-xl flex items-center justify-center text-2xl font-bold flex-shrink-0">
                        {letter}
                      </div>
                      <div className="flex-1 h-px bg-gray-200" />
                      <span className="text-xs text-gray-400 font-semibold">{groupedByLetter[letter].length} term{groupedByLetter[letter].length !== 1 ? 's' : ''}</span>
                    </div>

                    {/* Terms */}
                    <div className="space-y-4">
                      {groupedByLetter[letter].map(term => (
                        <div
                          key={term.term}
                          id={term.term.toLowerCase().replace(/[^a-z0-9]+/g, '-')}
                          className="bg-white border border-gray-200 rounded-xl p-6 hover:border-[#D97642] transition-colors group"
                        >
                          <div className="flex items-start justify-between gap-4 mb-3">
                            <h2 className="text-lg font-bold text-gray-900 group-hover:text-[#D97642] transition-colors">
                              {term.term}
                            </h2>
                            {term.tag && (
                              <span className={`text-xs font-bold px-2.5 py-1 rounded-full whitespace-nowrap flex-shrink-0 ${TAG_COLORS[term.tag] || 'bg-gray-200 text-gray-700'}`}>
                                {term.tag}
                              </span>
                            )}
                          </div>

                          <p className="text-gray-700 text-sm leading-relaxed mb-3">{term.definition}</p>

                          {term.example && (
                            <div className="bg-[#F9F7F4] border-l-4 border-[#D97642] rounded-r-lg p-4 mb-3">
                              <p className="text-xs font-bold text-[#D97642] uppercase tracking-wide mb-1">Indiana Example</p>
                              <p className="text-sm text-gray-700 italic">{term.example}</p>
                            </div>
                          )}

                          {term.related && term.related.length > 0 && (
                            <div className="flex flex-wrap gap-2 mt-3">
                              <span className="text-xs text-gray-400 font-semibold">Related:</span>
                              {term.related.map(r => (
                                <span key={r} className="text-xs bg-gray-100 text-gray-600 px-2.5 py-1 rounded-full">{r}</span>
                              ))}
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </main>
        </div>
      </div>

      {/* CTA */}
      <section className="py-16 bg-[#F9F7F4]">
        <div className="max-w-4xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {[
              { to: '/resources/indiana-life-insurance-calculator', icon: 'ri-calculator-line', title: 'Coverage Calculator', desc: 'Find your exact coverage gap in 2 minutes', cta: 'Calculate Now' },
              { to: '/instant-quote', icon: 'ri-price-tag-3-line', title: 'Get Instant Quotes', desc: 'Compare rates from 5+ Indiana carriers', cta: 'See Rates' },
              { to: '/resources/life-insurance-riders-indiana', icon: 'ri-settings-3-line', title: 'Riders Guide', desc: 'Which policy add-ons are worth the cost?', cta: 'Read Guide' },
            ].map(item => (
              <Link key={item.to} to={item.to} className="group bg-white rounded-xl border border-gray-200 p-6 hover:border-[#D97642] hover:shadow-md transition-all text-center">
                <div className="w-12 h-12 bg-[#D97642]/10 text-[#D97642] rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:bg-[#D97642] group-hover:text-white transition-colors">
                  <i className={`${item.icon} text-2xl`} />
                </div>
                <h3 className="font-bold text-gray-900 mb-2 group-hover:text-[#D97642] transition-colors">{item.title}</h3>
                <p className="text-sm text-gray-500 mb-4">{item.desc}</p>
                <span className="text-sm font-semibold text-[#D97642] flex items-center justify-center gap-1 group-hover:gap-2 transition-all">
                  {item.cta}<i className="ri-arrow-right-line" />
                </span>
              </Link>
            ))}
          </div>

          <div className="bg-gradient-to-br from-[#1A3A52] to-[#0F2A3F] text-white rounded-2xl p-8 text-center">
            <h2 className="text-3xl font-bold mb-4">Still Have Questions?</h2>
            <p className="text-white/75 text-lg mb-8 max-w-xl mx-auto">Benjamin Wright is a licensed Indiana life insurance agent who explains everything in plain English — just like this glossary.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/book-appointment" className="inline-flex items-center justify-center gap-2 bg-white text-[#1A3A52] px-8 py-4 rounded-xl font-bold hover:bg-gray-50 transition-colors whitespace-nowrap">
                <i className="ri-calendar-line" />Schedule a Free Call
              </Link>
              <a href="tel:3176482983" className="inline-flex items-center justify-center gap-2 bg-white/10 text-white px-8 py-4 rounded-xl font-bold hover:bg-white/20 transition-colors border border-white/20 whitespace-nowrap">
                <i className="ri-phone-line" />Call (317) 648-2983
              </a>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
